import { Box, Button, Container, Link, Typography } from '@mui/material';
import { differenceWith, isEqual } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';
import { ROUTES } from 'routing';
import { AppForm, FormSubmitButton } from 'shared/components';
import { useDeleteUserMapping, useUpdateUserMapping } from 'shared/mutations';
import { useAssignedCommodityList, useCommodityList } from 'shared/queries';
import { Commodity, ObjectTyped, UserInfo, UserMapping } from 'shared/types';
import { commodityToUserMapping } from 'shared/utility';
import { CommoditiesDropdown } from './commodities-dropdown';
import { ResetUserMappingEditForm } from './reset-user-mapping-edit-form';

interface UserMappingPageProps {
  mapping: UserMapping;
  user: UserInfo;
}

export interface UpdateUserMappingForm {
  commodities: Commodity[];
}

function calculateAssignedCommodityDifferences(form: Commodity[], current: Commodity[]) {
  const createCommodities = differenceWith(form, current, isEqual);
  const deleteCommodities = differenceWith(current, form, isEqual);
  return { createCommodities, deleteCommodities } as const;
}

export function UserMappingEditPage({ mapping, user }: UserMappingPageProps) {
  const updateMutation = useUpdateUserMapping();
  const deleteMutation = useDeleteUserMapping();
  const assignedCommodities = useAssignedCommodities(mapping.id);

  const defaultForm: UpdateUserMappingForm = {
    commodities: assignedCommodities,
  };

  const submit = async (form: UpdateUserMappingForm) => {
    const isoCurrentDate = new Date().toISOString();
    const { createCommodities, deleteCommodities } = calculateAssignedCommodityDifferences(
      form.commodities,
      assignedCommodities
    );
    const mapper = commodityToUserMapping({
      createdBy: user.id,
      createdDate: isoCurrentDate,
      userMappingId: mapping.id,
      createdByEmail: user.email,
    });

    await updateMutation.mutateAsync({
      id: mapping.id,
      updatedDate: isoCurrentDate,
      updatedBy: user.id,
      updatedByEmail: user.email,
      createCommodities: createCommodities.map(mapper),
      deleteCommodities: deleteCommodities.map(mapper),
    });
  };

  const deleteMapping = async () => {
    if (window.confirm('Are you sure you want to delete this mapping?')) {
      await deleteMutation.mutateAsync(mapping);
    }
  };
  return (
    <Container sx={{ p: 2, height: 'calc(100vh - 125px)' }}>
      <Box sx={{ pb: 2 }}>
        <Link underline='none' component={RouterLink} to={ROUTES.userMapping}>
          Back to User Mapping List
        </Link>
      </Box>
      {Boolean(assignedCommodities.length) && (
        <AppForm id='user-mapping-edit-form' defaultForm={defaultForm} onSubmit={submit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant='h5'>Modify assigned commodities for {mapping.name}</Typography>
              {user.isInsightEmployee && (
                <Box>
                  <Button size='large' color='error' onClick={deleteMapping}>
                    delete mapping
                  </Button>
                </Box>
              )}
            </Box>
            <CommoditiesDropdown />
            <FormSubmitButton defaultForm={defaultForm} />
          </Box>
          <ResetUserMappingEditForm defaultForm={defaultForm} />
        </AppForm>
      )}
    </Container>
  );
}

function useAssignedCommodities(userMappingId: string) {
  const { commodities } = useCommodityList();
  const { assignedCommodities } = useAssignedCommodityList(userMappingId);
  if (!commodities || !assignedCommodities) {
    return [];
  }
  const map = ObjectTyped.fromEntries(assignedCommodities.map(mapping => [mapping.id, true]));

  const filteredCommodities = commodities.filter(commodity => map[`${userMappingId}${commodity.id}`]);

  return filteredCommodities;
}
